import React from 'react'
import { GenericError, Route, withRouter } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import zionDebug from '@fs/zion-debug'
import useAnonSession from './useAnonSession'
import useImagePermission from './useImagePermission'

// turn on in browser console with: localStorage.debug = 'fs:search:auth:*'
const debug = zionDebug('search:auth:imageAuthRoute')

export const ImageAuthWrapper = ({ location, children, LoadingSkeleton }) => {
  const { userLoading, signedIn } = useUser()
  const permission = useImagePermission(location?.pathname)

  if (signedIn || permission.isPublic) {
    debug('allowing route: signed in?', signedIn, 'public image?', permission.isPublic)
    return children
  }

  if (userLoading || permission.loading) {
    debug('loading: user loading?', userLoading, 'permission loading?', permission.loading)
    return LoadingSkeleton || null
  }

  debug('requiring sign in.')
  window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`)
  return LoadingSkeleton || null
}

/**
 * Route wrapper for Film Viewer (3:1) image arks. Uses the DAS permission endpoint to decide
 * access instead of the deprecated GedcomX collection lookup: a publicly-viewable image renders
 * for anyone, otherwise signed-in users render and anonymous users are sent to login.
 */
const ImageAuthRoute = withRouter(({ component: Component, children, LoadingSkeleton, location, ...rest }) => {
  const { sessionStarted, error } = useAnonSession()

  // If we can't get an anonymous session, require sign in
  if (error) {
    debug('Could not get anonymous session. Requiring sign in.')
    window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`)
    return <GenericError /> // user shouldn't get here; it's just here for testing purposes
  }

  // Show the skeleton during the anonymous-session bootstrap instead of a blank page.
  if (!sessionStarted) return LoadingSkeleton || null

  return (
    <Route {...rest}>
      {(props) => (
        <ImageAuthWrapper location={location} LoadingSkeleton={LoadingSkeleton}>
          {children || <Component {...props} />}
        </ImageAuthWrapper>
      )}
    </Route>
  )
})

export default ImageAuthRoute
